import { IconButton } from '@material-ui/core';
import { attributes, Collection, define, Events, Linked } from '@type-r/models';
import { useEvent, useIO, useLinked, useModel } from '@type-r/react';
import { CompanyModel, globalStore, InvoiceManagementPermission, localUserInfo, ParticipantRoles, PlatformCompany } from 'api';
import { InvoiceModel } from 'api/invoice';
import { asCurrency, Button, CollapsableSection, colors, Flex, FormCheckbox, Loading, PageHeaderTile, PageTileField, PopupIODialog, PrimaryButton, SearchInput, SelectSortBy, TilesList, Toolbar, useLinkedQueryParam } from 'lqx-react';
import * as React from 'react';
import { useEffect } from 'react';
import { CustomerSupplier, RowH2 } from '../../../../../common';
import { InvoiceTile, InvoiceTileHeader } from '../../../../../common/invoice-aging';
import { RecsInvoice } from '../../inmatch/api';
import { CreateRADialog } from '../../inmatch/invoices/createRADialog';
import { DebthSummary } from './api';
import { ShowSocDialog, SocDialogType } from './dialogs';
import { SocEmailButton, SocExportButton, SocLatestStatementSent } from './dialogs/report-actions';
import { Can } from 'src/app/services/can';
import { UserEntitlementCatalogs } from 'src/app/models/UserEntitlements';

const sortInvoicesOptions = {
    invoiceNumber: 'Invoice Number',
    invoiceDueDate: 'Invoice Due Date',
    invoicedAmount: 'Invoiced Amount',
    unreconciledAmount: 'Unreconciled Amount',
    displayStatus: 'Invoice Status',
    hasSocComments: 'Has SOA Comments'
};

const sumAmount = (items) => {
    return items.reduce((x, y) => x + y.amount, 0);
}
@define
class SoaState extends attributes({
    filter: '',
    sortBy: {
        name: 'invoiceNumber',
        isAsc: true
    },
    category: 'all',
    selected: Collection.ofRefs(InvoiceModel)
}) { }

export const CustomerDebthList = ({ customerDebth }: {
    customerDebth: DebthSummary
}) => {
    const state = useModel(SoaState);

    const $bucket = useLinkedQueryParam("bucket");
    const $hasPromiseToPay = useLinkedQueryParam("hasPromiseToPay");
    const $hasPromiseToPayOverdue = useLinkedQueryParam("hasPromiseToPayOverdue");
    const $hasBrokenPromise = useLinkedQueryParam("hasBrokenPromise");
    const $sentDateDesc = useLinkedQueryParam("sentDateDesc");
    const $lastUpdatedOnDesc = useLinkedQueryParam("lastUpdatedOnDesc");

    const $doIO = useLinked(false);

    const isReady = useIO(
        async () => await loadDetails(),
        [
            $doIO.value,
            customerDebth,
            state.filter,
            state.sortBy['_changeToken'],
            state.category,
            $hasPromiseToPay.value,
            $hasBrokenPromise.value,
            $sentDateDesc.value
        ]
    )

    const loadDetails = async () => {
        if ($bucket.value)
            state.category = String($bucket.value);

        let params = {
            category: state.category,
            searchString: state.filter,
            sortBy: state.sortBy.name,
            asc: state.sortBy.isAsc
        };
        if ($hasPromiseToPay.value) params['hasPromiseToPay'] = JSON.parse($hasPromiseToPay.value.toString());
        if ($hasPromiseToPayOverdue.value) params['hasPromiseToPayOverdue'] = JSON.parse($hasPromiseToPayOverdue.value.toString());
        if ($hasBrokenPromise.value) params['hasBrokenPromise'] = JSON.parse($hasBrokenPromise.value.toString());
        if ($sentDateDesc.value) {
            params.sortBy = 'invoiceSentDate';
            params.asc = false;
        }
        if ($lastUpdatedOnDesc.value) {
            params.sortBy = 'updatedOn';
            params.asc = false;
        }

        await Promise.all([
            customerDebth.fetch({ params: params }),
            customerDebth.recsInvoices.fetch({
                params: {
                    range: "all",
                    status: "",
                    customer: customerDebth.customer.id,
                    supplier: customerDebth.supplier.id
                }
            })
        ]);

        customerDebth.invoices.updateEach((i) => {
            const recsInv = customerDebth.recsInvoices.get(i.id);
            i.currency = recsInv && recsInv.currency;
            i.remittanceAdviceIds = recsInv && recsInv.remittanceAdviceIds;
            i.paymentInfoIds = recsInv && recsInv.paymentInfoIds;
            i.matchedPayments = recsInv && recsInv.matchedPayments;
        });
    }

    const refreshState = () => {
        state.selected.reset();
        $doIO.set(!$doIO.value);
    }

    useEvent(Events, "Soc:promiseToPayChanged", refreshState);
    useEvent(Events, "Soc:disputeAdded", refreshState);
    useEvent(Events, "Soc:statementEmailSent", () => $doIO.set(!$doIO.value));
    useEvent(Events, "Soc:internalStatusAdded", () => $doIO.set(!$doIO.value));

    useEffect(() => {
        state.filter = '';
        state.selected.reset();
        state.sortBy.isAsc = true;
        state.sortBy.name = 'invoiceNumber';
    }, [customerDebth.id]);

    const $category = state.$.category
        .onChange(() => state.filter = '');

    let hasCustomerWriteAccess = false;
    let hasCustomerApproverAccess = false;
    let hasSupplierWriteAccess = false;
    let hasSupplierApproverAccess = false;
    let hasSuperSupplierAccess = false;
    let showEmailAction = false;
    let conversationId = -1

    const sortedLength = customerDebth.invoices ? customerDebth.invoices.length : 0;
    if (sortedLength > 0) {
        conversationId = customerDebth.invoices.models[0].conversationId;
    }

    if (conversationId > 0) {
        hasCustomerWriteAccess = localUserInfo.hasSomePermissions("InvoiceManagement", ParticipantRoles.Customer,
            [InvoiceManagementPermission.CustomerWrite], conversationId);
        hasCustomerApproverAccess = localUserInfo.hasSomePermissions("InvoiceManagement", ParticipantRoles.Customer,
            [InvoiceManagementPermission.CustomerApprover], conversationId);
        hasSupplierWriteAccess = localUserInfo.hasSomePermissions("InvoiceManagement", ParticipantRoles.Supplier,
            [InvoiceManagementPermission.SupplierWrite], conversationId);
        hasSupplierApproverAccess = localUserInfo.hasSomePermissions("InvoiceManagement", ParticipantRoles.Supplier,
            [InvoiceManagementPermission.SupplierApprover], conversationId);
        hasSuperSupplierAccess = localUserInfo.hasSomePermissions("InvoiceManagement", ParticipantRoles.Supplier,
            [InvoiceManagementPermission.SupplierSuperSupplier], conversationId);
        showEmailAction = localUserInfo.hasSomePermissions("InvoiceManagement", ParticipantRoles.Supplier,
            [InvoiceManagementPermission.SupplierApprover, InvoiceManagementPermission.SupplierWrite, InvoiceManagementPermission.SupplierSuperSupplier], conversationId);
    }

    const showInternalStatus = hasSuperSupplierAccess || hasSupplierWriteAccess || hasSupplierApproverAccess;
    const showComments = hasCustomerWriteAccess || hasSuperSupplierAccess || hasSupplierWriteAccess || hasSupplierApproverAccess;
    const showPromiseToPay = hasCustomerWriteAccess || hasSuperSupplierAccess;
    const showCreateRa = hasCustomerWriteAccess || hasSuperSupplierAccess || hasSupplierWriteAccess || hasSupplierApproverAccess;
    const showDispute = hasCustomerWriteAccess || hasCustomerApproverAccess || hasCustomerWriteAccess || hasSuperSupplierAccess || hasSupplierWriteAccess || hasSupplierApproverAccess;
    const showApprove = $category.value == 'disputed'

    const isSelected = state.selected && state.selected.length > 0;

    const canChangePromiseToPay = isSelected && state.selected.every(i => i.canBeAccepted || i.canEditPromiseToPay)
    const isDisputedWithCustomer = isSelected && state.selected.every(i => i.disputeStatus == 'DisputedWithCustomer');
    const isDisputedWithSupplier = isSelected && state.selected.every(i => i.disputeStatus == 'DisputedWithSupplier');
    const isEscalatedInternally = isSelected && state.selected.every(i => i.isEscalatedInternally == true);

    let isApproveAllowed = false;
    if (isEscalatedInternally) {
        //only customer approver can approve disputed escalated invoices
        if (isDisputedWithCustomer && hasCustomerApproverAccess && !hasCustomerWriteAccess) {
            isApproveAllowed = true
        }
        //invoices escalated by supplier write should be able to be approved by supplier approver
        if (hasSupplierApproverAccess && !hasSupplierWriteAccess) {
            isApproveAllowed = true
        }
    } else {
        //each customer and super supplier can approve disputed non escalated invoices
        if (isDisputedWithCustomer && (hasCustomerApproverAccess || hasCustomerWriteAccess || hasSuperSupplierAccess)) {
            isApproveAllowed = true
        }

        //Supplier Write and Supplier Approver users should be able to dispute or approve invoices which are Disputed by super supplier.
        if (isDisputedWithSupplier && (hasSupplierWriteAccess || hasSupplierApproverAccess || hasSuperSupplierAccess)) {
            isApproveAllowed = true;
        }
    }

    const isDisputedWithSupplierAndHasCustomerAccess = (isDisputedWithSupplier && (hasCustomerWriteAccess || hasCustomerApproverAccess));
    const isDisputedWithCustomerAndHasSupplierAccess = (isDisputedWithCustomer && (hasSupplierWriteAccess || hasSupplierApproverAccess));
    const isEscalatedAndHasCustomerWriteAccess = (hasCustomerWriteAccess && isEscalatedInternally);

    const isCommentingDisabled = !isSelected || isDisputedWithSupplier;
    const isPromiseToPayDisabled = !canChangePromiseToPay || isDisputedWithSupplier || isEscalatedAndHasCustomerWriteAccess;
    const isDisputeDisabled = !isSelected || isDisputedWithSupplierAndHasCustomerAccess || isEscalatedAndHasCustomerWriteAccess || isDisputedWithCustomerAndHasSupplierAccess;
    const isApproveDisabled = !isApproveAllowed || isDisputedWithSupplierAndHasCustomerAccess || (isDisputedWithCustomerAndHasSupplierAccess && hasSuperSupplierAccess);

    useEvent(Events, "Soc:commentAdded", () => state.selected.reset());
    useEvent(Events, "Soc:approveAdded", () => state.selected.reset());

    const $showCreateRA = useLinked(false);
    const createRA = () => $showCreateRA.true();

    const companies = new Collection<PlatformCompany>([]);

    const toRecsInvoices = (invoiceModels: Collection<InvoiceModel>): Collection<RecsInvoice> => {
        const recsInvoices = invoiceModels.map((invModel) => {
            let recsInv = new RecsInvoice(invModel);
            recsInv.supplierPlatformCompanyId = +customerDebth.supplier.id;
            recsInv.customerPlatformCompanyId = +customerDebth.customer.id;
            return recsInv;
        });

        return new Collection<RecsInvoice>(recsInvoices);
    }

    const $dialog = useLinked<SocDialogType>(null);

    const rel = globalStore.getRelationshipById(customerDebth.id as any);

    return !isReady ? <Loading /> :
        <>
            <PageHeaderTile
                toolbar={
                    <Toolbar>
                        <SocLatestStatementSent customerDepth={customerDebth} />
                        <Flex grow />
                        {
                            showInternalStatus ?
                                <>
                                    <Button onClick={() => { $dialog.set("internalStatus"); }}>
                                        Internal Status
                                    </Button>
                                </>
                                : <></>
                        }
                        {
                            showComments ?
                                <>
                                    <Button onClick={() => { $dialog.set("comment"); }} disabled={isCommentingDisabled}>
                                        Add Comments
                                    </Button>
                                </>
                                : <></>
                        }
                        {
                            showPromiseToPay ?
                                <>
                                    <PrimaryButton onClick={() => $dialog.set("promiseToPay")} disabled={isPromiseToPayDisabled}>
                                        Promise to Pay
                                    </PrimaryButton>
                                </>
                                : <></>
                        }
                        {
                            showDispute ?
                                <>
                                    <Button onClick={() => { $dialog.set("dispute"); }} disabled={isDisputeDisabled}>
                                        Dispute
                                    </Button>
                                </>
                                : <></>

                        }
                        {
                            showCreateRa && Can(UserEntitlementCatalogs.BankEntryWrite) ?
                                <>
                                    <PrimaryButton onClick={createRA} disabled={!isSelected}>
                                        Create RA
                                    </PrimaryButton>
                                </>
                                : <></>
                        }
                        {
                            showApprove ?
                                <>
                                    <Button onClick={() => { $dialog.set("approve"); }} disabled={isApproveDisabled}>
                                        Approve
                                    </Button>
                                </>
                                : <></>
                        }

                        <SocExportButton customerDepth={customerDebth}></SocExportButton>

                        {showEmailAction ? <SocEmailButton customerDepth={customerDebth}></SocEmailButton> : <></>}

                        <ShowSocDialog
                            model={state.selected}
                            $dialog={$dialog}
                            hasCustomerApproverAccess={hasCustomerApproverAccess}
                            hasCustomerWriteAccess={hasCustomerWriteAccess}
                            hasSupplierApproverAccess={hasSupplierApproverAccess}
                            hasSupplierWriteAccess={hasSupplierWriteAccess}
                            hasSuperSupplierAccess={hasSuperSupplierAccess} />

                        {$showCreateRA.value ? <CreateRADialog $isOpen={$showCreateRA} companies={companies} invoices={toRecsInvoices(state.selected)} onSuccess={refreshState} withAppLink /> : undefined}
                    </Toolbar>
                }
                color={colors["green"]}
                // title={<CustomerSupplier customer={customerDebth.customer} supplier={customerDebth.supplier} />}
            >
                <Flex column>
                    <RowH2>Business Partner Id: {customerDebth.relationship.Customer.externalRelationshipId}</RowH2>
                    <Flex row wrap>
                        <PageTileField label="Total" $checked={$category.equals('all')}>
                            {asCurrency(customerDebth.amount)} ({customerDebth.count})
                        </PageTileField>
                        <PageTileField label="Overdue" $checked={$category.equals('overdue')}>
                            {asCurrency(customerDebth.overdueAmount)} ({customerDebth.overdueCount})
                        </PageTileField>
                        <PageTileField label="Due in 2 weeks" $checked={$category.equals('due2weeks')}>
                            {asCurrency(customerDebth.due2weeks)} ({customerDebth.due2weeksCount})
                        </PageTileField>
                        <PageTileField label="Due in 2+ weeks" $checked={$category.equals('due2plus')}>
                            {asCurrency(customerDebth.due2plusWeeks)} ({customerDebth.due2plusWeeksCount})
                        </PageTileField>
                        <PageTileField label="Disputed" $checked={$category.equals('disputed')}>
                            {asCurrency(customerDebth.disputed)} ({customerDebth.disputedCount})
                        </PageTileField>
                    </Flex>
                </Flex>
            </PageHeaderTile>

            <CollapsableSection title={`Invoices`}>
                <Flex row>
                    {
                        (sortedLength < 1000) && <FormCheckbox
                            $checked={
                                Linked.value(
                                    sortedLength ? sortedLength === state.selected.length : false,
                                    x => x ? state.selected.set(customerDebth.invoices.models) : state.selected.reset()
                                )
                            }
                        />
                    }

                    <SearchInput
                        placeholder="Filter by Invoice Number, Amount, Due Date, POP Date, Unreconciled Amount, Status"
                        $value={state.$.filter}
                    />

                    <SelectSortBy
                        $attr={state.sortBy.$.name}
                        $asc={state.sortBy.$.isAsc}
                        options={sortInvoicesOptions}
                    />
                </Flex>

                <InvoiceTileHeader />
                <TilesList
                    items={customerDebth.invoices.models}
                    itemSize={42}
                    height={300}
                    renderer={InvoiceTile(state.selected as any /* HACK */, '/remittance-advices/unmatched/', true, true)}
                    selected={undefined /* selected */}
                />
            </CollapsableSection>
        </>
}